import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import { useAppDispatch } from './store/hooks';
import { initializeWishlistRequest } from './store/wishlist';
import ScrollToTop from './components/common/ScrollToTop';

const Home = React.lazy(() => import('./pages/Home'));
const AboutUs = React.lazy(() => import('./pages/AboutUs'));
const PlaceholderPage = React.lazy(() => import('./pages/PlaceholderPage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const ProductCatalogPage = React.lazy(() => import('./pages/ProductCatalogPage'));
const SingleProduct = React.lazy(() => import('./pages/SingleProduct'));
const QuotationPage = React.lazy(() => import('./pages/Quotation/QuotationPage'));
const StoresPage = React.lazy(() => import('./pages/Stores'));
const Login = React.lazy(() => import('./pages/Login'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const MyAccount = React.lazy(() => import('./pages/MyAccount'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const Wishlist = React.lazy(() => import('./pages/Wishlist'));
const ResetPasswordConfirm = React.lazy(() => import('./pages/ResetPasswordConfirm'));
const ComparisonPage = React.lazy(() => import('./pages/Comparison/ComparisonPage'));

const RouteLoader: React.FC = () => (
  <div className="flex min-h-[45vh] items-center justify-center px-4">
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-slate-300 shadow-xl shadow-black/20">
      <span className="h-3 w-3 animate-ping rounded-full bg-indigo-400" />
      <span className="text-sm font-semibold tracking-wide">Loading</span>
    </div>
  </div>
);

const App: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(initializeWishlistRequest());
  }, [dispatch]);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <MainLayout>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/shop" element={<Navigate to="/laptop" replace />} />
            <Route path="/product-category/:category" element={<ProductCatalogPage />} />
            <Route path="/laptop" element={<ProductCatalogPage />} />
            <Route path="/laptop/:category" element={<ProductCatalogPage />} />
            <Route path="/desktop" element={<ProductCatalogPage />} />
            <Route path="/desktop/:category" element={<ProductCatalogPage />} />
            <Route path="/components" element={<ProductCatalogPage />} />
            <Route path="/components/:category" element={<ProductCatalogPage />} />
            <Route path="/accessories" element={<ProductCatalogPage />} />
            <Route path="/accessories/:category" element={<ProductCatalogPage />} />
            <Route path="/printers" element={<ProductCatalogPage />} />
            <Route path="/printers/:category" element={<ProductCatalogPage />} />
            <Route path="/projectors" element={<ProductCatalogPage />} />
            <Route path="/projectors/:category" element={<ProductCatalogPage />} />
            <Route path="/pos-system" element={<ProductCatalogPage />} />
            <Route path="/pos-system/:category" element={<ProductCatalogPage />} />
            <Route path="/software" element={<ProductCatalogPage />} />
            <Route path="/software/:category" element={<ProductCatalogPage />} />
            <Route path="/used-items" element={<ProductCatalogPage />} />
            <Route path="/used-items/:category" element={<ProductCatalogPage />} />
            <Route path="/offers" element={<ProductCatalogPage />} />
            <Route path="/offers/:category" element={<ProductCatalogPage />} />
            <Route path="/product/:slug" element={<SingleProduct />} />
            <Route path="/contact" element={<PlaceholderPage title="Contact" />} />
            <Route path="/track-order" element={<PlaceholderPage title="Track Order" />} />
            <Route path="/support" element={<PlaceholderPage title="Support" />} />
            <Route path="/stores" element={<StoresPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/comparison" element={<ComparisonPage />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/quotation" element={<QuotationPage />} />
            <Route path="/quote" element={<Navigate to="/quotation" replace />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ForgotPassword />} />
            <Route path="/my-account" element={<MyAccount />} />
            <Route path="/account" element={<Navigate to="/my-account" replace />} />
            <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </MainLayout>
    </BrowserRouter>
  );
};

export default App;
